if (!customElements.get('sticky-cart')) {
  customElements.define(
    'sticky-cart',
    class StickyCart extends HTMLElement {
      constructor() {
        super();

        this.triggerType = this.dataset.triggerType || 'scroll_past_button';
        this.triggerOffset = parseInt(this.dataset.triggerOffset, 10) || 500;
        this.showImage = this.dataset.showImage === 'true';
        this.loginForPrice = this.dataset.loginForPrice === 'true';
        this.selectOptionsText = this.dataset.selectOptionsText || 'Select options';

        this.imageSlot = this.querySelector('[data-sticky-cart-image]');
        this.titleSlot = this.querySelector('[data-sticky-cart-title]');
        this.priceSlot = this.querySelector('[data-sticky-cart-price]');
        this.quantitySlot = this.querySelector('[data-sticky-cart-quantity]');
        this.buttonSlot = this.querySelector('[data-sticky-cart-button]');
        this.errorSlot = this.querySelector('[data-sticky-cart-error]');

        this.mainProductSection = null;
        this.mainProductForm = null;
        this.mainAddToCartButton = null;
        this.mainQuantityInput = null;
        this.stickyQuantityInput = null;
        this.stickyAddToCartButton = null;
        this.buttonObserver = null;
        this.errorObserver = null;
        this.intersectionObserver = null;
        this.mediaObserver = null;
      }

      connectedCallback() {
        if (this.loginForPrice) {
          this.style.display = 'none';
          return;
        }

        if (!this.findProductElements()) {
          this.style.display = 'none';
          return;
        }

        this.render();
        this.setupVisibility();
        this.setupVariantListeners();
        this.setupResizeListener();
      }

      disconnectedCallback() {
        if (this.buttonObserver) this.buttonObserver.disconnect();
        if (this.errorObserver) this.errorObserver.disconnect();
        if (this.intersectionObserver) this.intersectionObserver.disconnect();
        if (this.mediaObserver) this.mediaObserver.disconnect();
        if (this.onScrollBound) window.removeEventListener('scroll', this.onScrollBound);
        if (this.onResizeBound) window.removeEventListener('resize', this.onResizeBound);
        if (this.onVariantChangeBound) {
          document.removeEventListener('variant:change', this.onVariantChangeBound);
        }
      }

      findProductElements() {
        this.mainProductSection = document.querySelector('.product-info-wrap');
        if (!this.mainProductSection) return false;

        this.mainProductForm = this.mainProductSection.querySelector('product-form');
        if (!this.mainProductForm) return false;

        this.mainAddToCartButton = this.mainProductForm.querySelector('.button-add-to-cart');
        if (!this.mainAddToCartButton) return false;

        this.mainQuantityInput = this.mainProductForm.querySelector('.quantity-input-field');
        return true;
      }

      render() {
        this.clearSlots();
        this.cloneImage();
        this.cloneTitle();
        this.clonePrice();

        if (this.hasVariantPicker()) {
          this.renderSelectOptionsButton();
        } else {
          this.cloneQuantity();
          this.cloneAddToCartButton();
        }

        this.syncButtonState();
        this.watchButtonState();
        this.watchErrorState();
        this.watchActiveMedia();
      }

      clearSlots() {
        [this.imageSlot, this.titleSlot, this.priceSlot, this.quantitySlot, this.buttonSlot, this.errorSlot].forEach(
          (slot) => {
            if (slot) slot.innerHTML = '';
          },
        );

        if (this.quantitySlot) this.quantitySlot.style.display = '';
      }

      hasVariantPicker() {
        return !!this.mainProductSection.querySelector('variant-radios, variant-selects');
      }

      cloneImage() {
        if (!this.showImage || !this.imageSlot) return;
        this.imageSlot.innerHTML = '';

        const mainImage = this.mainProductSection.querySelector('.product-media-main.is-active img');
        if (!mainImage) return;

        const img = document.createElement('img');
        img.src = mainImage.currentSrc || mainImage.src;
        img.alt = mainImage.alt || '';
        img.loading = 'lazy';
        this.imageSlot.appendChild(img);
      }

      cloneTitle() {
        const title = this.mainProductSection.querySelector('.product-info-heading');
        if (title && this.titleSlot) {
          this.titleSlot.textContent = title.textContent.trim();
        }
      }

      clonePrice() {
        if (!this.priceSlot) return;
        const price = this.mainProductSection.querySelector('[id^="price-"]');
        if (!price) return;

        const clone = price.cloneNode(true);
        clone.removeAttribute('id');
        this.priceSlot.innerHTML = '';
        this.priceSlot.appendChild(clone);
      }

      cloneQuantity() {
        if (!this.quantitySlot) return;
        const quantityInput = this.mainProductForm.querySelector('quantity-input');
        if (!quantityInput) return;

        const clone = quantityInput.cloneNode(true);
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        this.quantitySlot.appendChild(clone);
        this.stickyQuantityInput = clone.querySelector('.quantity-input-field');

        if (this.stickyQuantityInput && this.mainQuantityInput) {
          this.stickyQuantityInput.value = this.mainQuantityInput.value;

          const syncStickyToMain = () => {
            this.mainQuantityInput.value = this.stickyQuantityInput.value;
            this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          };

          const syncMainToSticky = () => {
            this.stickyQuantityInput.value = this.mainQuantityInput.value;
          };

          this.stickyQuantityInput.addEventListener('input', syncStickyToMain);
          this.stickyQuantityInput.addEventListener('change', syncStickyToMain);
          this.mainQuantityInput.addEventListener('input', syncMainToSticky);
          this.mainQuantityInput.addEventListener('change', syncMainToSticky);
        }
      }

      cloneAddToCartButton() {
        if (!this.buttonSlot) return;

        const clone = document.createElement('button');
        clone.type = 'button';
        clone.className = this.mainAddToCartButton.className;
        clone.innerHTML = this.mainAddToCartButton.innerHTML;
        clone.removeAttribute('id');

        clone.addEventListener('click', (event) => {
          event.preventDefault();
          if (clone.disabled) return;

          if (this.stickyQuantityInput && this.mainQuantityInput) {
            this.mainQuantityInput.value = this.stickyQuantityInput.value;
          }

          this.mainAddToCartButton.click();
          this.syncButtonState();
        });

        this.buttonSlot.appendChild(clone);
        this.stickyAddToCartButton = clone;
      }

      renderSelectOptionsButton() {
        if (this.quantitySlot) this.quantitySlot.style.display = 'none';
        if (!this.buttonSlot) return;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button-secondary button-select-options';
        button.textContent = this.selectOptionsText;
        button.addEventListener('click', () => {
          const productInfo = document.querySelector('.product-info');
          if (!productInfo) return;

          const stickyHeader = document.querySelector('sticky-header');
          const headerOffset = stickyHeader ? stickyHeader.offsetHeight : 0;
          const top = productInfo.getBoundingClientRect().top + window.pageYOffset - headerOffset - 20;

          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.buttonSlot.appendChild(button);
      }

      syncButtonState() {
        if (!this.stickyAddToCartButton || !this.mainAddToCartButton) return;

        const disabled =
          this.mainAddToCartButton.disabled || this.mainAddToCartButton.hasAttribute('disabled');

        this.stickyAddToCartButton.disabled = disabled;
        if (disabled) {
          this.stickyAddToCartButton.setAttribute('disabled', '');
        } else {
          this.stickyAddToCartButton.removeAttribute('disabled');
        }

        const mainText = this.mainAddToCartButton.querySelector('span');
        const stickyText = this.stickyAddToCartButton.querySelector('span');
        if (mainText && stickyText) {
          stickyText.textContent = mainText.textContent.trim();
        }

        const stickySpinner = this.stickyAddToCartButton.querySelector('.button-overlay-spinner');
        const isLoading = this.mainAddToCartButton.classList.contains('loading');

        this.stickyAddToCartButton.classList.toggle('loading', isLoading);
        if (stickySpinner) {
          stickySpinner.classList.toggle('hidden', !isLoading);
        }
      }

      watchButtonState() {
        if (!this.mainAddToCartButton) return;
        if (this.buttonObserver) this.buttonObserver.disconnect();

        this.buttonObserver = new MutationObserver(() => {
          this.syncButtonState();
          this.clonePrice();
          this.cloneImage();
        });

        this.buttonObserver.observe(this.mainAddToCartButton, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['class', 'disabled', 'aria-disabled'],
        });
      }

      watchErrorState() {
        const mainErrorWrapper = this.mainProductForm.querySelector('.product-form-error-message-wrapper');
        if (!mainErrorWrapper || !this.errorSlot) return;

        if (this.errorObserver) this.errorObserver.disconnect();

        const syncError = () => {
          const hidden = mainErrorWrapper.hasAttribute('hidden');
          this.errorSlot.innerHTML = hidden ? '' : mainErrorWrapper.innerHTML;
        };

        this.errorObserver = new MutationObserver(syncError);
        this.errorObserver.observe(mainErrorWrapper, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['hidden'],
        });

        syncError();
      }

      watchActiveMedia() {
        if (!this.showImage || !this.mainProductSection) return;
        const mediaGallery = this.mainProductSection.querySelector(
          '.product-media-main-wrapper, .product-media-gallery',
        );
        if (!mediaGallery) return;

        if (this.mediaObserver) this.mediaObserver.disconnect();

        this.mediaObserver = new MutationObserver((mutations) => {
          const changedActiveState = mutations.some((mutation) => {
            return mutation.type === 'attributes' && mutation.attributeName === 'class';
          });

          if (changedActiveState) {
            this.cloneImage();
          }
        });

        this.mediaObserver.observe(mediaGallery, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      setupVisibility() {
        if (this.triggerType === 'scroll_distance') {
          this.onScrollBound = () => {
            if (window.scrollY > this.triggerOffset) {
              this.show();
            } else {
              this.hide();
            }
          };

          window.addEventListener('scroll', this.onScrollBound, { passive: true });
          this.onScrollBound();
          return;
        }

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.hide();
              } else {
                this.show();
              }
            });
          },
          {
            threshold: 0.1,
          },
        );

        this.intersectionObserver.observe(this.mainAddToCartButton);
      }

      setupVariantListeners() {
        this.onVariantChangeBound = () => {
          window.setTimeout(() => {
            if (!this.findProductElements()) return;
            this.render();
          }, 120);
        };

        document.addEventListener('variant:change', this.onVariantChangeBound);
        document.addEventListener('change', (event) => {
          const target = event.target;
          if (!target) return;
          if (target.closest('variant-radios') || target.closest('variant-selects')) {
            this.onVariantChangeBound();
          }
        });
      }

      setupResizeListener() {
        this.onResizeBound = () => {
          if (this.classList.contains('is-visible')) {
            document.body.style.paddingBottom = this.offsetHeight + 'px';
          }
        };

        window.addEventListener('resize', this.onResizeBound);
      }

      show() {
        this.classList.add('is-visible');
        document.body.style.paddingBottom = this.offsetHeight + 'px';
      }

      hide() {
        this.classList.remove('is-visible');
        document.body.style.paddingBottom = '';
      }
    },
  );
}
